import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { isSimpleError, SimpleError } from '@simonbackx/simple-errors';
import { DataValidator, Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { Address } from '../../addresses/Address.js';
import { CountryHelper } from '../../addresses/CountryDecoder.js';
import { AuditLogReplacement, AuditLogReplacementType } from '../../AuditLogReplacement.js';
import { File } from '../../files/File.js';
import { Image } from '../../files/Image.js';
import { TranslatedString } from '../../TranslatedString.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../../upgradePriceFrom2To4DecimalPlaces.js';
import { RecordChoice, RecordSettings, RecordType, RecordWarning, RecordWarningType } from './RecordSettings.js';
export class RecordAnswer extends AutoEncoder {
    id;
    /**
     * Settings of this record at the time of input. Depending on the changes, we can auto migrate some settings
     */
    settings;
    /**
     * Date that this was changed. To determine merge order
     */
    date;
    /**
     * Date that this answer was last reviewed by the author
     * -> when editing by the organization, don't set this date
     */
    reviewedAt;
    isOutdated(timeoutMs) {
        if (!this.reviewedAt) {
            return true;
        }
        if (this.reviewedAt.getTime() < new Date().getTime() - timeoutMs) {
            return true;
        }
        return false;
    }
    markReviewed() {
        this.reviewedAt = new Date();
    }
    get stringValue() {
        return $t(`%Gr`);
    }
    get objectValue() {
        return this.stringValue;
    }
    /**
     * Include both the setting and the value
     */
    get descriptionValue() {
        return this.settings.name + ': ' + this.stringValue;
    }
    get excelColumns() {
        return this.settings.excelColumns;
    }
    get excelValues() {
        return [{
                value: this.stringValue,
                style: this.stringValue.includes('\n')
                    ? {
                        alignment: {
                            wrapText: true,
                        },
                    }
                    : undefined,
            }];
    }
    getWarnings() {
        if (!this.isEmpty) {
            try {
                this.validate();
            }
            catch (e) {
                if (isSimpleError(e)) {
                    return [
                        RecordWarning.create({
                            id: 'validation-warning-' + this.id,
                            text: TranslatedString.create(e.getHuman()),
                            type: RecordWarningType.Error,
                        }),
                    ];
                }
                // ignore
            }
        }
        return [];
    }
    validate() {
        // valid by default
    }
    /**
     * Return true when it is not the default value as a general rule
     * E.g. checkbox by default not checked -> empty if not checked
     */
    get isEmpty() {
        return false;
    }
    isReviewedAfter(answer) {
        if (this.reviewedAt && answer.reviewedAt) {
            return this.reviewedAt > answer.reviewedAt;
        }
        if (this.reviewedAt && !answer.reviewedAt) {
            return true;
        }
        if (!this.reviewedAt && answer.reviewedAt) {
            return false;
        }
        // Both null
        return false;
    }
    static createDefaultAnswer(settings) {
        const type = RecordAnswerDecoder.getClassForType(settings.type);
        return type.create({
            settings,
        });
    }
    getDiffName() {
        return this.settings.name;
    }
    getDiffValue() {
        if (this.settings.type === RecordType.Textarea) {
            return AuditLogReplacement.create({
                value: this.stringValue ?? undefined,
                type: AuditLogReplacementType.LongText,
            });
        }
        return this.stringValue;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], RecordAnswer.prototype, "id", void 0);
__decorate([
    field({ decoder: RecordSettings })
], RecordAnswer.prototype, "settings", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 128, defaultValue: () => new Date() })
], RecordAnswer.prototype, "date", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], RecordAnswer.prototype, "reviewedAt", void 0);
export class RecordAnswerDecoderStatic {
    decode(data) {
        const settings = data.field('settings').decode(RecordSettings);
        const type = settings.type;
        return data.decode(this.getClassForType(type));
    }
    getClassForType(type) {
        switch (type) {
            case RecordType.Checkbox: return RecordCheckboxAnswer;
            case RecordType.Text:
            case RecordType.Phone:
            case RecordType.Email:
            case RecordType.Textarea:
                return RecordTextAnswer;
            case RecordType.MultipleChoice: return RecordMultipleChoiceAnswer;
            case RecordType.ChooseOne: return RecordChooseOneAnswer;
            case RecordType.Address: return RecordAddressAnswer;
            case RecordType.Date: return RecordDateAnswer;
            case RecordType.Price: return RecordPriceAnswer;
            case RecordType.Image: return RecordImageAnswer;
            case RecordType.Integer: return RecordIntegerAnswer;
            case RecordType.File: return RecordFileAnswer;
        }
    }
}
export const RecordAnswerDecoder = new RecordAnswerDecoderStatic();
export class RecordTextAnswer extends RecordAnswer {
    value = null;
    get stringValue() {
        return this.value ?? '/';
    }
    get objectValue() {
        return this.value;
    }
    getWarnings() {
        const base = super.getWarnings();
        if (!this.settings.warning) {
            return base;
        }
        if (this.settings.warning.inverted) {
            return this.value === null || this.value.length === 0 ? [this.settings.warning, ...base] : base;
        }
        return this.value !== null && this.value.length > 0 ? [this.settings.warning, ...base] : base;
    }
    validate() {
        if (this.settings.required && (this.value === null || this.value.length === 0)) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%qt`),
                field: 'input',
            });
        }
        if (this.value && this.settings.name.toLocaleLowerCase().includes('rijksregisternummer')) {
            if (!DataValidator.verifyBelgianNationalNumber(this.value)) {
                throw new SimpleError({
                    code: 'invalid_field',
                    message: $t(`%1RF`, { number: this.value }),
                    field: 'input',
                });
            }
            // Auto format the number
            this.value = DataValidator.formatBelgianNationalNumber(this.value);
        }
    }
    get isEmpty() {
        return (this.value === null || this.value.length === 0);
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], RecordTextAnswer.prototype, "value", void 0);
export class RecordCheckboxAnswer extends RecordAnswer {
    selected = false;
    comments;
    getWarnings() {
        const base = super.getWarnings();
        if (!this.settings.warning) {
            return base;
        }
        if (this.settings.warning.inverted) {
            return !this.selected ? [this.settings.warning, ...base] : base;
        }
        return this.selected ? [this.settings.warning, ...base] : base;
    }
    get stringValue() {
        return this.selected ? $t(`%BM`) : $t(`%BN`);
    }
    get objectValue() {
        return this.selected;
    }
    get excelValues() {
        return [{
                value: this.selected ? (this.comments ? this.comments : $t(`%wo`)) : $t(`%18s`),
                format: null,
            }];
    }
    validate() {
        if (this.settings.required && !this.selected) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%qv`),
                field: 'input',
            });
        }
    }
    get isEmpty() {
        return !this.selected;
    }
}
__decorate([
    field({ decoder: BooleanDecoder })
], RecordCheckboxAnswer.prototype, "selected", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], RecordCheckboxAnswer.prototype, "comments", void 0);
export class RecordMultipleChoiceAnswer extends RecordAnswer {
    selectedChoices = [];
    get stringValue() {
        return this.selectedChoices.map(c => c.name).join(', ');
    }
    get objectValue() {
        return this.selectedChoices.map(c => c.id);
    }
    getWarnings() {
        const base = super.getWarnings();
        if (this.selectedChoices.length === 0) {
            return base;
        }
        const warnings = base;
        for (const choice of this.selectedChoices) {
            if (choice.warning && !choice.warning.inverted) {
                warnings.push(choice.warning);
            }
        }
        for (const choice of this.settings.choices) {
            if (choice.warning && choice.warning.inverted) {
                if (!this.selectedChoices.find(s => s.id === choice.id)) {
                    warnings.push(choice.warning);
                }
            }
        }
        return warnings;
    }
    validate() {
        if (this.settings.required && this.selectedChoices.length === 0) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%qw`),
                field: 'input',
            });
        }
    }
    get isEmpty() {
        return this.selectedChoices.length === 0;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(RecordChoice) })
], RecordMultipleChoiceAnswer.prototype, "selectedChoices", void 0);
export class RecordChooseOneAnswer extends RecordAnswer {
    selectedChoice = null;
    get selectedChoices() {
        if (this.selectedChoice === null) {
            return [];
        }
        return [this.selectedChoice];
    }
    get stringValue() {
        return this.selectedChoice?.name?.toString() ?? '/';
    }
    get objectValue() {
        return this.selectedChoice?.id ?? null;
    }
    getWarnings() {
        const base = super.getWarnings();
        if (this.selectedChoice === null) {
            // TODO: show warning if inverted
            return base;
        }
        const warnings = base;
        if (this.selectedChoice.warning && !this.selectedChoice.warning.inverted) {
            warnings.push(this.selectedChoice.warning);
        }
        for (const choice of this.settings.choices) {
            if (choice.warning && choice.warning.inverted) {
                if (this.selectedChoice.id !== choice.id) {
                    warnings.push(choice.warning);
                }
            }
        }
        return warnings;
    }
    validate() {
        if (this.settings.required && this.selectedChoice === null) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%qx`),
                field: 'input',
            });
        }
    }
    get isEmpty() {
        return this.selectedChoice === null;
    }
}
__decorate([
    field({ decoder: RecordChoice, nullable: true })
], RecordChooseOneAnswer.prototype, "selectedChoice", void 0);
export class RecordAddressAnswer extends RecordAnswer {
    address = null;
    get stringValue() {
        return this.address?.toString() ?? '/';
    }
    get objectValue() {
        return this.address?.encode({ version: 0 }) ?? null;
    }
    validate() {
        if (this.settings.required && this.address === null) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%yw`),
                field: 'input',
            });
        }
    }
    get excelValues() {
        return [
            {
                value: this.address ? `${this.address.street}` : null,
                format: null,
            },
            {
                value: this.address ? `${this.address.number}` : null,
                format: null,
            },
            {
                value: this.address?.postalCode ?? null,
                format: null,
            },
            {
                value: this.address?.city ?? null,
                format: null,
            },
            {
                value: this.address ? CountryHelper.getName(this.address.country) : null,
                format: null,
            },
        ];
    }
    get isEmpty() {
        return this.address === null;
    }
}
__decorate([
    field({ decoder: Address, nullable: true })
], RecordAddressAnswer.prototype, "address", void 0);
export class RecordDateAnswer extends RecordAnswer {
    dateValue = null;
    get stringValue() {
        return this.dateValue ? Formatter.dateNumber(this.dateValue, true) : '/';
    }
    get objectValue() {
        return this.dateValue;
    }
    validate() {
        if (this.settings.required && this.dateValue === null) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%yw`),
                field: 'input',
            });
        }
    }
    get isEmpty() {
        return this.dateValue === null;
    }
}
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], RecordDateAnswer.prototype, "dateValue", void 0);
export class RecordIntegerAnswer extends RecordAnswer {
    value = null;
    get stringValue() {
        return this.value !== null ? this.value.toString() : '/';
    }
    get objectValue() {
        return this.value;
    }
    getWarnings() {
        const base = super.getWarnings();
        if (!this.settings.warning) {
            return base;
        }
        if (this.settings.warning.inverted) {
            return this.value === null || this.value === 0 ? [this.settings.warning, ...base] : base;
        }
        return this.value !== null && this.value !== 0 ? [this.settings.warning, ...base] : base;
    }
    validate() {
        if (this.settings.required && (this.value === null)) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%qt`),
                field: 'input',
            });
        }
    }
    get isEmpty() {
        return (this.value === null);
    }
}
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], RecordIntegerAnswer.prototype, "value", void 0);
export class RecordPriceAnswer extends RecordAnswer {
    value = null;
    get stringValue() {
        return this.value !== null ? Formatter.price(this.value) : '/';
    }
    get objectValue() {
        return this.value;
    }
    getWarnings() {
        const base = super.getWarnings();
        if (!this.settings.warning) {
            return base;
        }
        if (this.settings.warning.inverted) {
            return this.value === null || this.value === 0 ? [this.settings.warning, ...base] : base;
        }
        return this.value !== null && this.value !== 0 ? [this.settings.warning, ...base] : base;
    }
    validate() {
        if (this.settings.required && (this.value === null)) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%qt`),
                field: 'input',
            });
        }
    }
    get isEmpty() {
        return (this.value === null);
    }
}
__decorate([
    field({ decoder: IntegerDecoder, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], RecordPriceAnswer.prototype, "value", void 0);
export class RecordImageAnswer extends RecordAnswer {
    image = null;
    get stringValue() {
        return this.image?.getPublicPath() ?? '/';
    }
    get objectValue() {
        return this.image?.encode({ version: 0 }) ?? null;
    }
    validate() {
        if (this.settings.required && this.image === null) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%yw`),
                field: 'input',
            });
        }
    }
    get isEmpty() {
        return this.image === null;
    }
    transformForDiff() {
        return this.image;
    }
}
__decorate([
    field({ decoder: Image, nullable: true })
], RecordImageAnswer.prototype, "image", void 0);
export class RecordFileAnswer extends RecordAnswer {
    file = null;
    get stringValue() {
        return this.file?.getPublicPath() ?? '/';
    }
    get objectValue() {
        return this.file?.encode({ version: 0 }) ?? null;
    }
    validate() {
        if (this.settings.required && this.file === null) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%yw`),
                field: 'input',
            });
        }
    }
    get isEmpty() {
        return this.file === null;
    }
    transformForDiff() {
        return this.file;
    }
}
__decorate([
    field({ decoder: File, nullable: true })
], RecordFileAnswer.prototype, "file", void 0);
//# sourceMappingURL=RecordAnswer.js.map